import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { PlusIcon, TrashIcon, SaveIcon, UserIcon, BuildingIcon, GlobeIcon, PenIcon, StarIcon, Settings, Moon, Crown, Lock, Zap, AlertTriangle, RefreshCw, Sparkles, Loader2, Pencil } from 'lucide-react';
import { Switch } from './ui/switch';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { DEFAULT_AI_MODEL, DEFAULT_AI_PROVIDER } from '@/config/aiDefaults';
import { isFeatureEnabled } from '@/config/featureFlags';
import { isAdminRole, isSystemAdminRole } from '@/lib/roles';
import ProfileConflictResolver, { ProfileConflict } from './ProfileConflictResolver';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { BUSINESS_TYPES, BUSINESS_TYPE_LABELS } from '@shared/schema';

interface UserProfile {
  userId: number;
  firstName: string;
  lastName: string;
  displayName: string;
  salutation: string;
  bio: string;
  businessName: string;
  businessType: string;
  businessDescription: string;
  headline: string;
  timezone: string;
  yearsExperience: number | null; // Legacy - prefer experienceStartYear
  experienceStartYear: number | null; // Year started in travel industry
  email: string;
  phone: string;
  website: string;
  city: string;
  state: string;
  zipCode: string;
  instagramHandle: string;
  facebookPage: string;
  twitterHandle: string;
  linkedinProfile: string;
  tiktokHandle: string;
  youtubeChannel: string;
  // worldvia.pro synced fields
  gemStatus: string;
  proUserRole: string;
  memberSince: string;
  destinationCities: string[];
  destinationCountries: string[];
  destinationRegions: string[];
  selectedSpecialties: string[];
  userSpecialties: string[];
  travelTypes: string[];
  clientInterests: string[];
  clientInterestCategories: string[];
  targetTripBudget: string[];
  supplierSpecialties: string[];
  // existing fields
  primarySpecialties: string[];
  secondarySpecialties: string[];
  destinationExpertise: string[];
  clientTypes: string[];
  preferredSuppliers: string[];
  certifications: any[];
  languages: string[];
  writingTone: string;
  communicationStyle: string;
  brandPersonality: string[];
  sampleBlogPosts: any[];
  sampleSocialPosts: any[];
  sampleEmails: any[];
  sampleItineraries: any[];
  contentTopics: string[];
  postingFrequency: string;
  preferredPostTypes: string[];
  aiAssistanceLevel: string;
  autoContextAwareness: boolean;
  contentPersonalization: boolean;
  isComplete: boolean;
}

interface ProfileCompletion {
  completion: number;
  sections: {
    basic: boolean;
    business: boolean;
    contact: boolean;
    specialties: boolean;
    content: boolean;
  };
  nextSteps: string[];
  isComplete: boolean;
}

interface UserPreferences {
  subscriptionTier: string;
  advancedMode: boolean;
  defaultProvider: string;
  defaultModel: string;
}

interface ChatProvider {
  provider: string;
  name: string;
  description: string;
  models: Array<{
    id: string;
    name: string;
    description: string;
    available: boolean;
  }>;
  available: boolean;
}

type ExampleType = 'blog' | 'social' | 'email' | 'itinerary';

export default function UserProfile() {
  const { user } = useAuth();
  const isSystemAdmin = isSystemAdminRole(user?.role);
  const isAdmin = isAdminRole(user?.role);
  const { isDarkMode, setIsDarkMode } = useTheme();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [completion, setCompletion] = useState<ProfileCompletion | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [newSpecialty, setNewSpecialty] = useState('');
  const [newDestination, setNewDestination] = useState('');
  const [newContentExample, setNewContentExample] = useState({ title: '', content: '', description: '' });
  const [editingExample, setEditingExample] = useState<{ type: ExampleType; index: number } | null>(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', content: '' });
  
  // Bio generation state
  const [isGeneratingBio, setIsGeneratingBio] = useState(false);
  
  // worldvia.pro sync conflict state
  const [conflicts, setConflicts] = useState<ProfileConflict[]>([]);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  
  // Fields synced from worldvia.pro (read-only in AIVIA)
  const READ_ONLY_FIELDS = ['firstName', 'lastName'] as const;

  // AR-291: the Default AI Model card stays hidden until the feature is live
  const showDefaultAiModel = isFeatureEnabled('profileDefaultAiModel');
  
  // Fetch user preferences
  const { data: preferences } = useQuery<UserPreferences>({
    queryKey: ['/api/user/preferences'],
    enabled: !!user,
  });

  // Fetch available providers - only consumed by the Default AI Model card (AR-291)
  const { data: availableProviders } = useQuery<ChatProvider[]>({
    queryKey: ['/api/chat/providers'],
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: showDefaultAiModel,
  });

  // Update preferences mutation
  const updatePreferencesMutation = useMutation({
    mutationFn: async (updates: Partial<UserPreferences>) => {
      return await apiRequest('PATCH', '/api/user/preferences', updates);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/preferences'] });
      toast({
        title: 'Preferences updated',
        description: 'Your preferences have been saved successfully.',
      });
    },
    onError: (error: any) => {
      console.error('Preferences update error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update preferences',
        variant: 'destructive',
      });
    },
  });

  useEffect(() => {
    fetchProfile();
    fetchCompletion();
    fetchConflicts();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch('/api/profile');
      const data = await response.json();
      setProfile(data.profile);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompletion = async () => {
    try {
      const response = await fetch('/api/profile/completion');
      const data = await response.json();
      setCompletion(data);
    } catch (error) {
      console.error('Error fetching completion:', error);
    }
  };

  const fetchConflicts = async () => {
    try {
      const response = await fetch('/api/profile/conflicts');
      const data = await response.json();
      setConflicts(data.conflicts || []);
    } catch (error) {
      console.error('Error fetching conflicts:', error);
    }
  };

  const resolveConflict = async (field: string, choice: 'aivia' | 'worldvia') => {
    const response = await fetch('/api/profile/conflicts/resolve', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ field, choice }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to resolve conflict');
    }
    
    // Refresh profile and conflicts after resolution
    await Promise.all([fetchProfile(), fetchConflicts()]);
  };

  const handleResolveAllAivia = async () => {
    for (const conflict of conflicts) {
      await resolveConflict(conflict.field, 'aivia');
    }
    setShowConflictDialog(false);
  };

  const isReadOnlyField = (field: string): boolean => {
    return READ_ONLY_FIELDS.includes(field as typeof READ_ONLY_FIELDS[number]);
  };

  const generateBio = async () => {
    setIsGeneratingBio(true);
    try {
      const response = await fetch('/api/profile/generate-bio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tone: profile?.writingTone || 'professional',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to generate bio');
      }

      const data = await response.json();
      if (data.bio) {
        if (profile) {
          updateProfile('bio', data.bio);
          toast({
            title: 'Bio generated!',
            description: 'Your AI-generated bio is ready. Feel free to edit it before saving.',
          });
        } else {
          // API succeeded but profile state is gone (user navigated away?)
          toast({
            title: 'Bio generated successfully',
            description: 'Please refresh the page to see your new bio.',
            variant: 'default',
          });
        }
      } else {
        // API succeeded but returned no bio content
        toast({
          title: 'Generation failed',
          description: 'The service returned an empty bio. Please try again.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error generating bio:', error);
      toast({
        title: 'Failed to generate bio',
        description: error.message || 'Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingBio(false);
    }
  };

  const saveProfile = async () => {
    if (!profile) return;
    
    setIsSaving(true);
    try {
      const response = await fetch('/api/profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profile)
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data.profile);
        await fetchCompletion();
        toast({
          title: 'Profile saved',
          description: 'Your profile changes have been saved successfully.',
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to save profile');
      }
    } catch (error: any) {
      console.error('Error saving profile:', error);
      toast({
        title: 'Error saving profile',
        description: error.message || 'Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const updateProfile = (field: keyof UserProfile, value: any) => {
    if (!profile) return;
    setProfile({ ...profile, [field]: value });
  };

  const addToArray = (field: keyof UserProfile, value: string) => {
    if (!profile || !value.trim()) return;
    const currentArray = (profile[field] as string[]) || [];
    if (!currentArray.includes(value.trim())) {
      updateProfile(field, [...currentArray, value.trim()]);
    }
  };

  const removeFromArray = (field: keyof UserProfile, index: number) => {
    if (!profile) return;
    const currentArray = (profile[field] as string[]) || [];
    updateProfile(field, currentArray.filter((_, i) => i !== index));
  };

  const addContentExample = async (type: 'blog' | 'social' | 'email' | 'itinerary') => {
    if (!newContentExample.title.trim() || !newContentExample.content.trim()) return;
    
    try {
      const response = await fetch(`/api/profile/examples/${type}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newContentExample)
      });
      
      if (response.ok) {
        setNewContentExample({ title: '', content: '', description: '' });
        await fetchProfile();
      }
    } catch (error) {
      console.error('Error adding content example:', error);
    }
  };

  const getExampleField = (type: ExampleType): keyof UserProfile => {
    switch (type) {
      case 'blog':
        return 'sampleBlogPosts';
      case 'social':
        return 'sampleSocialPosts';
      case 'email':
        return 'sampleEmails';
      case 'itinerary':
        return 'sampleItineraries';
      default:
        return 'sampleBlogPosts';
    }
  };

  const getExamples = (type: ExampleType) => {
    if (!profile) return [];
    const field = getExampleField(type);
    return ((profile as any)[field] as any[]) || [];
  };

  const openEditExample = (type: ExampleType, index: number, item: any) => {
    setEditingExample({ type, index });
    setEditForm({
      title: item?.title ?? '',
      description: item?.description ?? '',
      content: item?.content ?? '',
    });
  };

  const saveEditedExample = () => {
    if (!profile || !editingExample) return;
    const field = getExampleField(editingExample.type);
    const current = (((profile as any)[field] as any[]) || []).map((item: any, idx: number) =>
      idx === editingExample.index
        ? {
            ...item,
            title: editForm.title,
            description: editForm.description,
            content: editForm.content,
          }
        : item,
    );
    updateProfile(field, current);
    setEditingExample(null);
  };

  const removeExample = (type: ExampleType, index: number) => {
    if (!profile) return;
    const field = getExampleField(type);
    const current = (((profile as any)[field] as any[]) || []).filter((_, idx: number) => idx !== index);
    updateProfile(field, current);
  };

  const getScrollableListClass = (items: any[]) =>
    items.length > 3 ? 'space-y-2 max-h-64 overflow-y-auto pr-1' : 'space-y-2';

  // Get available models for selected provider
  const getAvailableModels = (provider: string) => {
    if (!availableProviders) return [];
    
    const providerData = availableProviders.find((p) => p.provider === provider);
    return providerData?.models || [];
  };

  // Handle provider change
  const handleProviderChange = (newProvider: string) => {
    const models = getAvailableModels(newProvider);
    const defaultModel = models.length > 0 ? models[0].id : DEFAULT_AI_MODEL;
    
    updatePreferencesMutation.mutate({
      defaultProvider: newProvider,
      defaultModel: defaultModel,
    });
  };

  // Handle model change
  const handleModelChange = (newModel: string) => {
    updatePreferencesMutation.mutate({
      defaultModel: newModel,
    });
  };

  const getTierBadgeVariant = (tier: string) => {
    switch (tier) {
      case 'PRO':
        return 'default';
      case 'FREE':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'PRO':
        return <Crown className="h-4 w-4" />;
      case 'FREE':
        return <Lock className="h-4 w-4" />;
      default:
        return <Settings className="h-4 w-4" />;
    }
  };

  if (isLoading) {
    return <div className="flex justify-center items-center h-64">Loading profile...</div>;
  }

  if (!profile) {
    return <div className="text-center">Error loading profile</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Header with completion status */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              {/* AR-210 (TC-18.4, TC-19.1): the page title is the page's h1 (CardTitle
                  always renders an h3); same classes as CardTitle. */}
              <h1 className="text-2xl font-semibold leading-none tracking-tight flex items-center gap-2">
                <UserIcon className="h-5 w-5" aria-hidden="true" />
                Your Travel Business Profile
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Help the AI understand your business and adapt to your style
              </p>
            </div>
            <Button onClick={saveProfile} disabled={isSaving} className="flex items-center gap-2 dark:bg-[#ff7540] dark:hover:bg-[#ea580c] dark:text-white">
              <SaveIcon className="h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </CardHeader>
        {completion && (
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span id="profile-completion-label" className="text-sm font-medium">Profile Completion</span>
                <span className="text-sm text-muted-foreground">{completion.completion}%</span>
              </div>
              <Progress value={completion.completion} aria-labelledby="profile-completion-label" className="h-2" />
              {completion.nextSteps.length > 0 && (
                <div className="text-sm text-muted-foreground dark:text-[#9CA8A9]">
                  Next: {completion.nextSteps.join(', ')}
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Conflict Banner */}
      {conflicts.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/20">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                <div>
                  <p className="font-medium text-amber-800 dark:text-amber-200">
                    Profile Sync Conflicts
                  </p>
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    {conflicts.length} field{conflicts.length !== 1 ? 's' : ''} differ from worldvia.pro
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowConflictDialog(true)}
                className="border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-800 dark:text-amber-300 dark:hover:bg-amber-900/50"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Review Changes
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Conflict Resolution Dialog */}
      <ProfileConflictResolver
        conflicts={conflicts}
        isOpen={showConflictDialog}
        onClose={() => setShowConflictDialog(false)}
        onResolve={resolveConflict}
        onResolveAll={handleResolveAllAivia}
      />

      {/* Profile tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* AR-210 (TC-41.3): below sm the six tabs scroll instead of overprinting, and the multi-column rows stack. */}
        <TabsList className="grid w-full grid-cols-6 max-sm:flex max-sm:justify-start max-sm:overflow-x-auto">
          <TabsTrigger value="basic" className="flex items-center gap-1">
            <UserIcon className="h-4 w-4" />
            Basic
          </TabsTrigger>
          <TabsTrigger value="business" className="flex items-center gap-1">
            <BuildingIcon className="h-4 w-4" />
            Business
          </TabsTrigger>
          <TabsTrigger value="specialties" className="flex items-center gap-1">
            <StarIcon className="h-4 w-4" />
            Specialties
          </TabsTrigger>
          <TabsTrigger value="content" className="flex items-center gap-1">
            <PenIcon className="h-4 w-4" />
            Content Style
          </TabsTrigger>
          <TabsTrigger value="examples" className="flex items-center gap-1">
            <GlobeIcon className="h-4 w-4" />
            Examples
          </TabsTrigger>
          <TabsTrigger value="preferences" className="flex items-center gap-1">
            <Settings className="h-4 w-4" />
            Preferences
          </TabsTrigger>
        </TabsList>

        {/* Basic Information Tab */}
        <TabsContent value="basic" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Name fields with Salutation */}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Label htmlFor="salutation" className="flex items-center gap-1.5">
                          Salutation
                          <Lock className="h-3 w-3 text-muted-foreground dark:text-[#FF9E7A]" />
                        </Label>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Synced from worldvia.pro</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Input
                    id="salutation"
                    value={profile.salutation || ''}
                    disabled
                    className="bg-muted/50"
                    placeholder="Mr/Mrs/Ms"
                  />
                </div>
                <div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Label htmlFor="firstName" className="flex items-center gap-1.5">
                          First Name
                          <Lock className="h-3 w-3 text-muted-foreground dark:text-[#FF9E7A]" />
                        </Label>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Synced from worldvia.pro</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Input
                    id="firstName"
                    value={profile.firstName ?? ''}
                    disabled
                    className="bg-muted/50"
                    placeholder="Your first name"
                  />
                </div>
                <div className="sm:col-span-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Label htmlFor="lastName" className="flex items-center gap-1.5">
                          Last Name
                          <Lock className="h-3 w-3 text-muted-foreground dark:text-[#FF9E7A]" />
                        </Label>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Synced from worldvia.pro</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Input
                    id="lastName"
                    value={profile.lastName ?? ''}
                    disabled
                    className="bg-muted/50"
                    placeholder="Your last name"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  id="displayName"
                  value={profile.displayName ?? ''}
                  onChange={(e) => updateProfile('displayName', e.target.value)}
                  placeholder="How you'd like to be addressed"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label htmlFor="bio">Professional Bio</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={generateBio}
                    disabled={isGeneratingBio}
                    className="flex items-center gap-1.5 h-7 text-xs dark:border-[#ff7540] dark:text-[#ff7540] dark:hover:bg-[#ff7540]/10"
                  >
                    {isGeneratingBio ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-3 w-3" />
                        Generate with AI
                      </>
                    )}
                  </Button>
                </div>
                <Textarea
                  id="bio"
                  value={profile.bio ?? ''}
                  onChange={(e) => updateProfile('bio', e.target.value)}
                  placeholder="Tell us about your background and experience in travel"
                  rows={4}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  {profile.bio ? `${profile.bio.length} characters` : 'AI can generate a bio based on your profile data'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profile.email ?? ''}
                    onChange={(e) => updateProfile('email', e.target.value)}
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={profile.phone ?? ''}
                    onChange={(e) => updateProfile('phone', e.target.value)}
                    placeholder="Your phone number"
                  />
                </div>
              </div>

              {/* Location - synced from worldvia.pro */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Label htmlFor="city" className="flex items-center gap-1.5">
                          City
                          <Lock className="h-3 w-3 text-muted-foreground dark:text-[#FF9E7A]" />
                        </Label>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Synced from worldvia.pro</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Input
                    id="city"
                    value={profile.city || ''}
                    disabled
                    className="bg-muted/50"
                    placeholder="City"
                  />
                </div>
                <div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Label htmlFor="state" className="flex items-center gap-1.5">
                          State
                          <Lock className="h-3 w-3 text-muted-foreground dark:text-[#FF9E7A]" />
                        </Label>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Synced from worldvia.pro</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Input
                    id="state"
                    value={profile.state || ''}
                    disabled
                    className="bg-muted/50"
                    placeholder="State"
                  />
                </div>
                <div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Label htmlFor="zipCode" className="flex items-center gap-1.5">
                          Zip
                          <Lock className="h-3 w-3 text-muted-foreground dark:text-[#FF9E7A]" />
                        </Label>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Synced from worldvia.pro</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Input
                    id="zipCode"
                    value={profile.zipCode || ''}
                    disabled
                    className="bg-muted/50"
                    placeholder="Zip"
                  />
                </div>
              </div>

              {/* WorldVia PRO Status */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Label htmlFor="gemStatus" className="flex items-center gap-1.5">
                          GEM Status
                          <Lock className="h-3 w-3 text-muted-foreground dark:text-[#FF9E7A]" />
                        </Label>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Synced from worldvia.pro</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Input
                    id="gemStatus"
                    value={profile.gemStatus || ''}
                    disabled
                    className="bg-muted/50"
                    placeholder="GEM Status"
                  />
                </div>
                <div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Label htmlFor="proUserRole" className="flex items-center gap-1.5">
                          PRO User Role
                          <Lock className="h-3 w-3 text-muted-foreground dark:text-[#FF9E7A]" />
                        </Label>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Synced from worldvia.pro</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Input
                    id="proUserRole"
                    value={profile.proUserRole || ''}
                    disabled
                    className="bg-muted/50"
                    placeholder="PRO Role"
                  />
                </div>
                <div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Label htmlFor="memberSince" className="flex items-center gap-1.5">
                          Member Since
                          <Lock className="h-3 w-3 text-muted-foreground dark:text-[#FF9E7A]" />
                        </Label>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Synced from worldvia.pro</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Input
                    id="memberSince"
                    value={profile.memberSince ? new Date(profile.memberSince).toLocaleDateString() : ''}
                    disabled
                    className="bg-muted/50"
                    placeholder="Member Since"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Business Information Tab */}
        <TabsContent value="business" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Business Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="businessName">Business Name</Label>
                <Input
                  id="businessName"
                  value={profile.businessName ?? ''}
                  onChange={(e) => updateProfile('businessName', e.target.value)}
                  placeholder="Your travel business name"
                />
              </div>

              <div>
                <Label htmlFor="businessType">Legal Entity Type</Label>
                {/* AR-392: business_type is the legal entity type; the options are
                    the shared vocabulary so this page and the Account center agree. */}
                <Select value={profile.businessType ?? ''} onValueChange={(value) => updateProfile('businessType', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your legal entity type" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map((value) => (
                      <SelectItem key={value} value={value}>{BUSINESS_TYPE_LABELS[value]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="businessDescription">Business Description</Label>
                <Textarea
                  id="businessDescription"
                  value={profile.businessDescription ?? ''}
                  onChange={(e) => updateProfile('businessDescription', e.target.value)}
                  placeholder="Describe your travel business and what makes it unique"
                  rows={4}
                />
              </div>

              {/* Headline and Timezone - synced from worldvia.pro */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Label htmlFor="headline" className="flex items-center gap-1.5">
                          Headline
                          <Lock className="h-3 w-3 text-muted-foreground dark:text-[#FF9E7A]" />
                        </Label>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Synced from worldvia.pro</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Input
                    id="headline"
                    value={profile.headline || ''}
                    disabled
                    className="bg-muted/50"
                    placeholder="Your tagline"
                  />
                </div>
                <div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Label htmlFor="timezone" className="flex items-center gap-1.5">
                          Time Zone
                          <Lock className="h-3 w-3 text-muted-foreground dark:text-[#FF9E7A]" />
                        </Label>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Synced from worldvia.pro</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Input
                    id="timezone"
                    value={profile.timezone || ''}
                    disabled
                    className="bg-muted/50"
                    placeholder="Your timezone"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="experienceStartYear">Year Started in Travel Industry</Label>
                <Select 
                  value={profile.experienceStartYear?.toString() || ''} 
                  onValueChange={(value) => updateProfile('experienceStartYear', value ? parseInt(value) : null)}
                >
                  <SelectTrigger id="experienceStartYear">
                    <SelectValue placeholder="Select year you started" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Generate years from current year back 75 years (1-based: +1) */}
                    {(() => {
                      const currentYear = new Date().getFullYear();
                      return Array.from({ length: 76 }, (_, i) => {
                        const year = currentYear - i;
                        // 1-based semantics: started this year = 1 year
                        const yearsExp = currentYear - year + 1;
                        return (
                          <SelectItem key={year} value={year.toString()}>
                            {year} ({yearsExp} {yearsExp === 1 ? 'year' : 'years'})
                          </SelectItem>
                        );
                      });
                    })()}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground mt-1">
                  {profile.experienceStartYear
                    ? (() => {
                        const currentYear = new Date().getFullYear();
                        const years =
                          currentYear - profile.experienceStartYear + 1;
                        return `${years} ${
                          years === 1 ? 'year' : 'years'
                        } of experience`;
                      })()
                    : 'Select when you started to calculate experience'}
                </p>
              </div>

              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={profile.website ?? ''}
                  onChange={(e) => updateProfile('website', e.target.value)}
                  placeholder="https://yourwebsite.com"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="instagramHandle">Instagram Handle</Label>
                  <Input
                    id="instagramHandle"
                    value={profile.instagramHandle ?? ''}
                    onChange={(e) => updateProfile('instagramHandle', e.target.value)}
                    placeholder="@yourusername"
                  />
                </div>
                <div>
                  <Label htmlFor="facebookPage">Facebook Page</Label>
                  <Input
                    id="facebookPage"
                    value={profile.facebookPage ?? ''}
                    onChange={(e) => updateProfile('facebookPage', e.target.value)}
                    placeholder="Facebook page URL"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Specialties Tab */}
        <TabsContent value="specialties" className="space-y-4">
          {/* worldvia.pro Synced Specialties & Expertise */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Synced from worldvia.pro
                <Lock className="h-4 w-4 text-muted-foreground" />
              </CardTitle>
              <CardDescription>
                These specialties and preferences are synced from your worldvia.pro profile
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Destinations */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-muted-foreground">Regions</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(profile.destinationRegions || []).length > 0 ? (
                      (profile.destinationRegions || []).map((item, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{String(item)}</Badge>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground italic">Not set</span>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Countries</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(profile.destinationCountries || []).length > 0 ? (
                      (profile.destinationCountries || []).map((item, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{String(item)}</Badge>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground italic">Not set</span>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Cities</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(profile.destinationCities || []).length > 0 ? (
                      (profile.destinationCities || []).map((item, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{String(item)}</Badge>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground italic">Not set</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Specialties */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Specialties</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(profile.selectedSpecialties || []).length > 0 ? (
                      (profile.selectedSpecialties || []).map((item, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{String(item)}</Badge>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground italic">Not set</span>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Travel Types</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(profile.travelTypes || []).length > 0 ? (
                      (profile.travelTypes || []).map((item, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{String(item)}</Badge>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground italic">Not set</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Client Interests */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Client Interests</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(profile.clientInterests || []).length > 0 ? (
                      (profile.clientInterests || []).map((item, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{String(item)}</Badge>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground italic">Not set</span>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Interest Categories</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(profile.clientInterestCategories || []).length > 0 ? (
                      (profile.clientInterestCategories || []).map((item, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{String(item)}</Badge>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground italic">Not set</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Budget & Suppliers */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Target Trip Budget</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(profile.targetTripBudget || []).length > 0 ? (
                      (profile.targetTripBudget || []).map((item, i) => (
                        <Badge key={i} variant="default" className="text-xs">{String(item)}</Badge>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground italic">Not set</span>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Supplier Specialties</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(profile.supplierSpecialties || []).length > 0 ? (
                      (profile.supplierSpecialties || []).map((item, i) => (
                        <Badge key={i} variant="default" className="text-xs">{String(item)}</Badge>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground italic">Not set</span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Editable Travel Specialties */}
          <Card>
            <CardHeader>
              <CardTitle>Travel Specialties</CardTitle>
              <CardDescription>Add your own specialties (these are editable)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Primary Specialties</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newSpecialty}
                    onChange={(e) => setNewSpecialty(e.target.value)}
                    placeholder="Add a specialty"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addToArray('primarySpecialties', newSpecialty);
                        setNewSpecialty('');
                      }
                    }}
                  />
                  <Button
                    onClick={() => {
                      addToArray('primarySpecialties', newSpecialty);
                      setNewSpecialty('');
                    }}
                    size="sm"
                  >
                    <PlusIcon className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(profile.primarySpecialties || []).map((specialty, index) => (
                    <Badge key={index} variant="default" className="flex items-center gap-1">
                      {specialty}
                      <button
                        onClick={() => removeFromArray('primarySpecialties', index)}
                        className="ml-1 hover:text-destructive"
                      >
                        <TrashIcon className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <Label>Destination Expertise</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newDestination}
                    onChange={(e) => setNewDestination(e.target.value)}
                    placeholder="Add a destination"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addToArray('destinationExpertise', newDestination);
                        setNewDestination('');
                      }
                    }}
                  />
                  <Button
                    onClick={() => {
                      addToArray('destinationExpertise', newDestination);
                      setNewDestination('');
                    }}
                    size="sm"
                  >
                    <PlusIcon className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(profile.destinationExpertise || []).map((destination, index) => (
                    <Badge key={index} variant="secondary" className="flex items-center gap-1">
                      {destination}
                      <button
                        onClick={() => removeFromArray('destinationExpertise', index)}
                        className="ml-1 hover:text-destructive"
                      >
                        <TrashIcon className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Content Style Tab */}
        <TabsContent value="content" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Content Style & Voice</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="writingTone">Writing Tone</Label>
                <Select value={profile.writingTone ?? ''} onValueChange={(value) => updateProfile('writingTone', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your writing tone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="friendly">Friendly</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                    <SelectItem value="enthusiastic">Enthusiastic</SelectItem>
                    <SelectItem value="authoritative">Authoritative</SelectItem>
                    <SelectItem value="conversational">Conversational</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="communicationStyle">Communication Style</Label>
                <Select value={profile.communicationStyle ?? ''} onValueChange={(value) => updateProfile('communicationStyle', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your communication style" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direct">Direct & Concise</SelectItem>
                    <SelectItem value="detailed">Detailed & Comprehensive</SelectItem>
                    <SelectItem value="storytelling">Storytelling</SelectItem>
                    <SelectItem value="educational">Educational</SelectItem>
                    <SelectItem value="inspirational">Inspirational</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="aiAssistanceLevel">AI Assistance Level</Label>
                <Select value={profile.aiAssistanceLevel ?? ''} onValueChange={(value) => updateProfile('aiAssistanceLevel', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="How much AI assistance do you prefer?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minimal">Minimal - Just basic suggestions</SelectItem>
                    <SelectItem value="moderate">Moderate - Balanced assistance</SelectItem>
                    <SelectItem value="comprehensive">Comprehensive - Full AI support</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Examples Tab */}
        <TabsContent value="examples" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Content Examples</CardTitle>
              <p className="text-sm text-muted-foreground">
                Provide examples of your writing style to help AI match your voice
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Add New Content Example</Label>
                <div className="space-y-3">
                  <Input
                    value={newContentExample.title}
                    onChange={(e) => setNewContentExample({ ...newContentExample, title: e.target.value })}
                    placeholder="Content title"
                  />
                  <Input
                    value={newContentExample.description}
                    onChange={(e) => setNewContentExample({ ...newContentExample, description: e.target.value })}
                    placeholder="Brief description"
                  />
                  <Textarea
                    value={newContentExample.content}
                    onChange={(e) => setNewContentExample({ ...newContentExample, content: e.target.value })}
                    placeholder="Paste your content example here..."
                    rows={4}
                  />
                  <div className="flex gap-2">
                    <Button onClick={() => addContentExample('blog')} size="sm" variant="outline">
                      Add as Blog Post
                    </Button>
                    <Button onClick={() => addContentExample('social')} size="sm" variant="outline">
                      Add as Social Post
                    </Button>
                    <Button onClick={() => addContentExample('email')} size="sm" variant="outline">
                      Add as Email
                    </Button>
                    <Button onClick={() => addContentExample('itinerary')} size="sm" variant="outline">
                      Add as Itinerary
                    </Button>
                  </div>
                </div>
              </div>

              {/* Display existing examples */}
              {(profile.sampleBlogPosts || []).length > 0 && (
                <div>
                  <Label>Blog Post Examples</Label>
                  <div className={getScrollableListClass(profile.sampleBlogPosts || [])}>
                    {(profile.sampleBlogPosts || []).map((post, index) => (
                      <Card key={index} className="p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium">{post.title}</h4>
                            <p className="text-sm text-muted-foreground">{post.description}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditExample('blog', index, post)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeExample('blog', index)}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
              {(profile.sampleSocialPosts || []).length > 0 && (
                <div>
                  <Label>Social Post Examples</Label>
                  <div className={getScrollableListClass(profile.sampleSocialPosts || [])}>
                    {(profile.sampleSocialPosts || []).map((post, index) => (
                      <Card key={index} className="p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium">{post.title}</h4>
                            <p className="text-sm text-muted-foreground">{post.description}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditExample('social', index, post)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeExample('social', index)}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
              {(profile.sampleEmails || []).length > 0 && (
                <div>
                  <Label>Email Examples</Label>
                  <div className={getScrollableListClass(profile.sampleEmails || [])}>
                    {(profile.sampleEmails || []).map((post, index) => (
                      <Card key={index} className="p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium">{post.title}</h4>
                            <p className="text-sm text-muted-foreground">{post.description}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditExample('email', index, post)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeExample('email', index)}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
              {(profile.sampleItineraries || []).length > 0 && (
                <div>
                  <Label>Itinerary Examples</Label>
                  <div className={getScrollableListClass(profile.sampleItineraries || [])}>
                    {(profile.sampleItineraries || []).map((post, index) => (
                      <Card key={index} className="p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium">{post.title}</h4>
                            <p className="text-sm text-muted-foreground">{post.description}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditExample('itinerary', index, post)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeExample('itinerary', index)}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Preferences Tab */}
        <TabsContent value="preferences" className="space-y-4">
          {/* Theme Preferences */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Moon className="h-4 w-4" />
                Theme Preferences
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">Dark Mode</Label>
                  <p className="text-sm text-gray-600">
                    {isDarkMode ? 'Dark theme is enabled' : 'Light theme is enabled'}
                  </p>
                </div>
                <Switch
                  checked={isDarkMode}
                  onCheckedChange={setIsDarkMode}
                />
              </div>
            </CardContent>
          </Card>

          {isSystemAdmin && (
            <>
              {/* Subscription Tier Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {getTierIcon(preferences?.subscriptionTier || 'FREE')}
                    Subscription Tier
                  </CardTitle>
                  <CardDescription>
                    Your current subscription tier determines available features
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <Badge variant={getTierBadgeVariant(preferences?.subscriptionTier || 'FREE')}>
                        {preferences?.subscriptionTier || 'FREE'}
                      </Badge>
                      <p className="text-sm text-gray-600 mt-2">
                        {preferences?.subscriptionTier === 'PRO' 
                          ? 'You have access to advanced features including provider and model selection.'
                          : 'Upgrade to Pro to access advanced features like provider and model selection.'
                        }
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Advanced Mode Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    Advanced Mode
                  </CardTitle>
                  <CardDescription>
                    Enable advanced features like provider and model selection
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base font-medium">
                        Advanced Mode
                      </Label>
                      <p className="text-sm text-gray-600">
                        {preferences?.advancedMode 
                          ? 'Advanced features are enabled'
                          : 'Advanced features are disabled'
                        }
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Debug: Role={user?.role}, Tier={preferences?.subscriptionTier}, AdvancedMode={preferences?.advancedMode?.toString()}, Disabled={preferences?.subscriptionTier !== 'PRO' && !isAdmin}
                      </p>
                    </div>
                    <Switch
                      checked={preferences?.advancedMode || false}
                      disabled={preferences?.subscriptionTier !== 'PRO' && !isAdmin}
                      onCheckedChange={(checked) => {
                        if (preferences?.subscriptionTier !== 'PRO' && !isAdmin) {
                          toast({
                            title: 'Pro subscription required',
                            description: 'Advanced mode is only available for Pro subscribers.',
                            variant: 'destructive',
                          });
                          return;
                        }
                        updatePreferencesMutation.mutate({ advancedMode: checked });
                      }}
                    />
                  </div>
                  {preferences?.subscriptionTier === 'FREE' && !isAdmin && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">
                        <Lock className="h-4 w-4 inline mr-1" />
                        Advanced mode requires a Pro subscription
                      </p>
                    </div>
                  )}
                  {preferences?.subscriptionTier === 'FREE' && isAdmin && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-600">
                        <Crown className="h-4 w-4 inline mr-1" />
                        Admin override: You can enable advanced mode regardless of subscription tier
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {/* Default Model Selection Card - gated by the profileDefaultAiModel flag (AR-291) */}
          {showDefaultAiModel && (
          <Card>
            <CardHeader>
              <CardTitle>Default AI Model</CardTitle>
              <CardDescription>
                Choose your preferred AI provider and model for new conversations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Provider</Label>
                <Select
                  value={preferences?.defaultProvider || DEFAULT_AI_PROVIDER}
                  onValueChange={handleProviderChange}
                  disabled={
                    !preferences?.advancedMode ||
                    (preferences?.subscriptionTier === 'FREE' && !isAdmin)
                  }
                >
                  <SelectTrigger
                    className={
                      preferences?.advancedMode &&
                      (preferences?.subscriptionTier === 'PRO' || isAdmin)
                        ? ''
                        : 'opacity-50'
                    }
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableProviders?.map((provider) => (
                      <SelectItem key={provider.provider} value={provider.provider}>
                        <div className="flex items-center gap-2">
                          <span>{provider.name || provider.provider}</span>
                          <Badge variant="outline" className="text-xs">
                            {provider.provider}
                          </Badge>
                          {!provider.available && (
                            <Badge variant="destructive" className="text-xs">
                              Unavailable
                            </Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Model</Label>
                <Select
                  value={preferences?.defaultModel || DEFAULT_AI_MODEL}
                  onValueChange={handleModelChange}
                  disabled={
                    !preferences?.advancedMode ||
                    (preferences?.subscriptionTier === 'FREE' && !isAdmin)
                  }
                >
                  <SelectTrigger
                    className={
                      preferences?.advancedMode &&
                      (preferences?.subscriptionTier === 'PRO' || isAdmin)
                        ? ''
                        : 'opacity-50'
                    }
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableModels(
                      preferences?.defaultProvider || DEFAULT_AI_PROVIDER,
                    ).map((model) => (
                      <SelectItem key={model.id} value={model.id}>
                        <div className="flex flex-col">
                          <span>{model.name}</span>
                          <span className="text-sm text-gray-500">{model.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
          )}
        </TabsContent>
      </Tabs>

      <Dialog
        open={!!editingExample}
        onOpenChange={(open) => {
          if (!open) {
            setEditingExample(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit content example</DialogTitle>
            <DialogDescription>
              Update the title, description, or content for this example.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title</Label>
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              />
            </div>
            <div>
              <Label>Content</Label>
              <Textarea
                rows={6}
                value={editForm.content}
                onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingExample(null)}>
              Cancel
            </Button>
            <Button onClick={saveEditedExample}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
} 
